import { Eye, EyeOff, Loader2 } from "lucide-react";

import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { useState } from "react";
import type { AxiosInstance } from "axios";

import { apiErrorMessage } from "../lib/api.js";
import { Logo } from "../components/Logo.js";

/**
 * Sign in.
 *
 * THE ERROR MESSAGE IS WHATEVER THE SERVER SAYS, and the server says one thing for every
 * failure — "Invalid email or password" — whether the address is unknown, the password is
 * wrong, the account is disabled, or the address was never verified. Inventing a friendlier
 * client-side message per case would undo the enumeration defence the API was built with.
 */
export function LoginScreen({
  client,
  onSignedIn,
}: {
  readonly client: AxiosInstance;
  readonly onSignedIn: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.post("/auth/login", { email, password });
      onSignedIn();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not sign in."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-(--page) px-4 py-10">
      {/*
        The blurred colour fields behind the card, in legacy's two brand colours. Purely
        decorative and therefore `aria-hidden` and `pointer-events-none`: a screen reader
        announcing two empty divs, or a stray click landing on one, is a cost with no
        matching benefit.
      */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-amber-400 opacity-20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-blue-500 opacity-20 blur-3xl" />
      </div>

      <div className="absolute top-8 left-8 z-10">
        <Logo className="h-7 w-auto" />
      </div>

      <div className="bg-card relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-xl md:flex-row">
        {/* The pitch. Hidden below `md` rather than stacked: on a phone it would push the
            form itself below the fold, and someone opening a login screen came to log in. */}
        <div className="hidden flex-col justify-center gap-6 bg-gradient-to-b from-blue-700 to-indigo-800 p-10 text-center text-white md:flex md:w-1/2">
          <h1 className="text-4xl leading-tight font-extrabold">Grow Your Brand</h1>
          <p className="text-lg text-blue-100">
            Access powerful analytics, campaigns and insights to scale your marketing effortlessly.
          </p>
          <div className="flex justify-center">
            <span className="rounded-full bg-white/20 px-6 py-2 text-sm font-medium backdrop-blur-sm">
              Trusted by 500+ Marketing Teams
            </span>
          </div>
        </div>

        <div className="w-full p-8 md:w-1/2 md:p-10">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold">Sign In</h2>
              <p className="text-muted-foreground mt-2 text-sm">Welcome back! Please login to continue.</p>
            </div>

            <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
              <div className="grid gap-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@company.com"
                  required
                  className="h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    required
                    className="h-11 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {/*
                    A reveal toggle, as legacy has. `type="button"` is load-bearing: inside
                    a form a bare <button> defaults to submit, so tapping the eye would
                    attempt a sign-in with whatever is typed so far.
                  */}
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1 transition-colors"
                  >
                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>
              </div>

              <div className="-mt-1 text-right">
                <a href="/forgot-password" className="text-primary text-sm font-medium hover:underline">
                  Forgot password?
                </a>
              </div>

              {error !== null && (
                <p className="text-destructive text-sm" role="alert">
                  {error}
                </p>
              )}

              <Button type="submit" disabled={busy} className="h-11 w-full">
                {busy && <Loader2 className="animate-spin" />}
                {busy ? "Signing in…" : "Sign In"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
